import { useEffect, useMemo } from 'react';
import { Typography, Tabs } from '../../ui';
import { useSearchParams } from 'react-router-dom';
import { usePermission } from '../../store/auth';
import { RetailersListPage } from '../retailers/RetailersListPage';
import { FarmersListPage } from '../farmers/FarmersListPage';
import { UsersPage } from '../admin/UsersPage';

/**
 * Unified "People" page — Retailers, Farmers/Customers and Users in one place via tabs.
 * Each tab is permission-gated and hosts the existing list page (embedded, so its own
 * title is hidden). The active tab is reflected in the URL (?tab=) so it's linkable and
 * the old /retailers, /farmers, /admin/users routes can redirect straight to the right tab.
 */
export function PeoplePage() {
  const [sp, setSp] = useSearchParams();
  const canRetailers = usePermission('RETAILER.VIEW');
  const canFarmers = usePermission('FARMER.VIEW');
  const canUsers = usePermission('USER.VIEW');

  const items = useMemo(
    () =>
      [
        canRetailers && { key: 'retailers', label: 'Retailers', children: <RetailersListPage embedded /> },
        canFarmers && { key: 'farmers', label: 'Farmers / Customers', children: <FarmersListPage embedded /> },
        canUsers && { key: 'users', label: 'Staffs', children: <UsersPage embedded /> },
      ].filter(Boolean) as { key: string; label: string; children: JSX.Element }[],
    [canRetailers, canFarmers, canUsers],
  );

  const requested = sp.get('tab');
  const activeKey = items.some((t) => t.key === requested) ? requested! : items[0]?.key;

  // Normalise the URL when the requested tab is missing/invalid (e.g. a stale
  // deep link, or one the user lacks permission for) so it stays shareable.
  useEffect(() => {
    if (activeKey && requested !== activeKey) setSp({ tab: activeKey }, { replace: true });
  }, [activeKey, requested, setSp]);

  return (
    <div>
      <Typography.Title level={4}>People</Typography.Title>
      <Tabs
        activeKey={activeKey}
        onChange={(k) => setSp({ tab: k }, { replace: true })}
        items={items}
      />
    </div>
  );
}
